import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@insight-health-ai/roseate/components';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import {
  completeEncounter,
  updateEncounter,
} from '../../store/actions/encounter';

import { CheckOutlined } from '@ant-design/icons';
import TemplateSelectDropdown from '../Settings/Settings.TemplateDropdown';
import { Tooltip } from 'antd';
import { Bars } from 'react-loader-spinner';
import { analytics } from 'src/services';

const GenerateSummaryButton = ({
  encounter,
  encounterPhase,
  handleCompleteRecording,
}) => {
  const isAfterVisitDisabled =
    encounterPhase === 'after-visit' &&
    encounter?.in_visit_status != 'completed';
  const { isEntitled: isAfterVisitGenerationAllowed } =
    usePermissionEntitlements('encounter.after-visit.generate');
  const buttonText =
    encounterPhase === 'in-visit'
      ? 'Generate note'
      : encounterPhase === 'after-visit'
        ? 'Generate document'
        : 'Save pre-chart';

  let tooltipText;
  if (!isAfterVisitGenerationAllowed) {
    tooltipText = 'You do not have permission to perform this action.';
  } else if (isAfterVisitDisabled) {
    tooltipText = 'AVS can be generated after in-visit note is generated.';
  }

  const button = (
    <Button
      id="generate_note"
      block
      onClick={handleCompleteRecording}
      disabled={isAfterVisitDisabled || !isAfterVisitGenerationAllowed}
      icon={<CheckOutlined />}
      size="lg"
    >
      {buttonText}
    </Button>
  );

  return isAfterVisitDisabled ? (
    <Tooltip title={tooltipText} placement="bottom">
      <span className="w-full">{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

const AfterVisitControls = ({
  encounterId,
  encounter,
  schedulepage,
  encounterPhase,
  restrictTemplates,
  storedParams,
  currentTemplate,
  setCurrentTemplate,
  templateId,
  templateOverridesId,
  handleCompleteRecording,
}) => {
  const dispatch = useDispatch();
  const handleGenerateSummary = () => {
    dispatch(
      updateEncounter(
        encounterId,
        encounterPhase,
        {
          template_id: currentTemplate?.template_id,
          template_overrides_id: currentTemplate?.template_overrides_id,
        },
        storedParams,
        false
      )
    );
    handleCompleteRecording();
  };

  return (
    <div className="w-full">
      <label className="text-[#667085] p-2">Document template</label>
      <TemplateSelectDropdown
        encounterPhase={encounterPhase}
        allowInlineEdit={true}
        showToast={false}
        currentTemplate={currentTemplate}
        setCurrentTemplate={setCurrentTemplate}
        templateId={templateId}
        templateOverridesId={templateOverridesId}
        fromAuraPage={!schedulepage}
        restrictTemplates={restrictTemplates}
      />
      <div className="flex space-x-4 mt-4 justify-center">
        <GenerateSummaryButton
          encounter={encounter}
          encounterPhase={encounterPhase}
          handleCompleteRecording={handleGenerateSummary}
        />
      </div>
    </div>
  );
};

const AfterVisitButton = ({
  setActiveTab,
  storedParams,
  selectedRecord,
  encounterStatus,
  encounterPhase,
  setEncounterStatus,
  setActiveRightPanelTab,
  setIsStreaming,
  setIsRecording,
  schedulepage,
  restrictTemplates,
}) => {
  const { user } = useAuth();

  const { isEntitled: hasICD10Access } = useFeatureEntitlements('icd-10');

  const { encounterDetails, mobileViewStatus } = useSelector(
    (state) => state?.encounterState
  );

  const dispatch = useDispatch();
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateId, _setTemplateId] = useState(null);
  const [templateOverridesId, _setTemplateOverridesId] = useState(null);

  const record =
    selectedRecord !== undefined ? selectedRecord : mobileViewStatus;

  //Function when complete recording is clicked
  const handleCompleteRecording = () => {
    setEncounterStatus('summary_inprogress');

    dispatch(
      completeEncounter(record?.encounter_id, encounterPhase, storedParams)
    );
    setIsStreaming(false);
    setIsRecording(false);
    setActiveTab?.('note');
    const noteType =
      encounterPhase === 'in-visit'
        ? 'Note'
        : encounterPhase === 'after-visit'
          ? 'AVS'
          : 'Pre-chart';

    if (hasICD10Access && encounterPhase == 'in-visit') {
      setActiveRightPanelTab && setActiveRightPanelTab('icd10');
    }

    if (schedulepage) {
      analytics.track(`Clicked Generate ${noteType} In Ambient From VCA`, {
        email: user?.email,
      });
    } else {
      analytics.track(`Clicked Generate ${noteType} In Ambient From Aura`, {
        email: user?.email,
      });
    }
  };

  return encounterStatus ? (
    <>
      <div
        style={{ maxWidth: 'min(400px, 100%)', minWidth: 'min(320px, 100%)' }}
        className="rounded-xl flex flex-col items-center justify-center relative current p-3"
      >
        <div className="w-full flex justify-center items-center">
          {encounterPhase === 'after-visit' ? (
            <AfterVisitControls
              encounter={encounterDetails}
              encounterId={record?.encounter_id}
              schedulepage={schedulepage}
              encounterPhase={encounterPhase}
              encounterStatus={encounterStatus}
              restrictTemplates={restrictTemplates}
              currentTemplate={currentTemplate}
              setCurrentTemplate={setCurrentTemplate}
              templateId={templateId}
              templateOverridesId={templateOverridesId}
              handleCompleteRecording={handleCompleteRecording}
              storedParams={storedParams}
            />
          ) : null}
        </div>
      </div>
    </>
  ) : (
    <>
      <Bars
        height="25"
        width="25"
        color="#00D090"
        ariaLabel="bars-loading"
        wrapperStyle={{}}
        wrapperClass="justify-center"
        visible={true}
      />
    </>
  );
};

export default AfterVisitButton;
